import { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, Monitor, Smartphone, Trash2, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '../../services/api';
import EmailBody from '../mail/EmailBody';
import {
  SIGNATURE_TEMPLATES, DEFAULT_ACCENT, emptyFields, generateSignatureHtml,
} from '../../lib/signatures';

const selectClass =
  'w-full h-10 rounded-md border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring/30';

const FIELD_LABELS = [
  ['name', 'Ad soyad', 'Ad Soyad'],
  ['title', 'Ünvan', 'Operasyon Yöneticisi'],
  ['company', 'Şirket', 'Şirket adı'],
  ['phone', 'Telefon', '+90 212 000 00 00'],
  ['email', 'E-posta', 'ad@sirket.com'],
  ['website', 'Web sitesi', 'www.sirket.com'],
];

const toDraft = (sig, user) => ({
  id: sig?.id ?? null,
  name: sig?.name ?? 'Yeni imza',
  template: sig ? sig.template || 'custom' : 'corporate',
  fields: { ...emptyFields(user), ...(sig?.fields || {}) },
  html: sig?.html ?? '',
});

const htmlOf = (draft) => (draft.template === 'custom' ? draft.html : generateSignatureHtml(draft.template, draft.fields));

const pickSaved = (sig) => ({
  name: sig.name,
  html: sig.html,
  template: sig.template,
  fields: sig.fields,
  is_default_new: sig.is_default_new,
  is_default_reply: sig.is_default_reply,
});

export const SignaturesSection = ({ user }) => {
  const [signatures, setSignatures] = useState(null);
  const [draft, setDraft] = useState(null);
  const [editorTab, setEditorTab] = useState('visual');
  const [previewMode, setPreviewMode] = useState('desktop');
  const [isSaving, setIsSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const load = useCallback(async () => {
    const res = await api.getSignatures();
    const list = res.signatures || [];
    setSignatures(list);
    return list;
  }, []);

  useEffect(() => {
    load()
      .then((list) => setDraft(toDraft(list[0] || null, user)))
      .catch((err) => toast.error('İmzalar yüklenemedi', { description: err.message }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saved = useMemo(() => (draft?.id && signatures ? signatures.find((s) => s.id === draft.id) : null), [draft?.id, signatures]);
  const currentHtml = draft ? htmlOf(draft) : '';
  const isNew = draft && !draft.id;
  const dirty = Boolean(
    draft && (isNew || !saved || saved.name !== draft.name.trim() || saved.html !== currentHtml),
  );

  const select = (sig) => {
    setDraft(toDraft(sig, user));
    setEditorTab('visual');
  };

  const startNew = () => {
    if (signatures && signatures.length >= 10) {
      toast.error('En fazla 10 imza oluşturabilirsiniz');
      return;
    }
    setDraft(toDraft(null, user));
    setEditorTab('visual');
  };

  const setField = (key, value) => setDraft((d) => ({ ...d, fields: { ...d.fields, [key]: value } }));

  const chooseTemplate = (templateId) => {
    setDraft((d) => ({ ...d, template: templateId }));
    setEditorTab('visual');
  };

  const openHtmlTab = () => {
    // HTML sekmesine geçerken mevcut görünümün HTML'i düzenlenmeye hazır olsun
    setDraft((d) => (d.template === 'custom' ? d : { ...d, html: htmlOf(d) }));
    setEditorTab('html');
  };

  const editHtml = (value) => setDraft((d) => ({ ...d, html: value, template: 'custom' }));

  const save = async () => {
    if (!draft.name.trim()) {
      toast.error('İmza adı boş olamaz');
      return;
    }
    if (!currentHtml.trim()) {
      toast.error('İmza içeriği boş olamaz');
      return;
    }
    setIsSaving(true);
    try {
      const body = {
        name: draft.name.trim(),
        html: currentHtml,
        template: draft.template,
        fields: draft.template === 'custom' ? null : draft.fields,
        // ilk imza otomatik olarak varsayılan olur; sonrakilerde mevcut varsayılanlar korunur
        is_default_new: saved ? saved.is_default_new : signatures.length === 0,
        is_default_reply: saved ? saved.is_default_reply : signatures.length === 0,
      };
      const result = saved ? await api.updateSignature(saved.id, body) : await api.createSignature(body);
      await load();
      setDraft(toDraft(result, user));
      setSavedAt(new Date());
      toast.success('İmza kaydedildi');
    } catch (err) {
      toast.error('İmza kaydedilemedi', { description: err.message });
    } finally {
      setIsSaving(false);
    }
  };

  const discard = () => setDraft(toDraft(saved ?? signatures[0] ?? null, user));

  const remove = async () => {
    if (!saved || !window.confirm(`"${saved.name}" imzası silinsin mi?`)) return;
    try {
      await api.deleteSignature(saved.id);
      const list = await load();
      setDraft(toDraft(list[0] || null, user));
      toast.success('İmza silindi');
    } catch (err) {
      toast.error('İmza silinemedi', { description: err.message });
    }
  };

  // Varsayılanlar: kayıtlı imzalar üzerinden değiştirilir (taslaktaki kaydedilmemiş düzenlemeler etkilenmez)
  const applyDefault = async (kind, value) => {
    const flag = kind === 'new' ? 'is_default_new' : 'is_default_reply';
    const target = value ? Number(value) : null;
    const current = signatures.find((s) => s[flag]);
    try {
      if (target) {
        const sig = signatures.find((s) => s.id === target);
        await api.updateSignature(target, { ...pickSaved(sig), [flag]: true });
      } else if (current) {
        await api.updateSignature(current.id, { ...pickSaved(current), [flag]: false });
      }
      await load();
      toast.success('Varsayılan kullanım güncellendi');
    } catch (err) {
      toast.error('Güncellenemedi', { description: err.message });
    }
  };

  if (!signatures || !draft) {
    return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-muted-foreground" /></div>;
  }

  const defaultNew = signatures.find((s) => s.is_default_new)?.id ?? '';
  const defaultReply = signatures.find((s) => s.is_default_reply)?.id ?? '';
  const previewDoc = `<p style="margin:0 0 12px">Merhaba,</p><p style="margin:0 0 16px">Görüşmemiz doğrultusunda hazırladığımız teklifi ekte paylaşıyorum.</p><p style="margin:0 0 12px">İyi çalışmalar,</p>${currentHtml}`;

  return (
    <div className="space-y-6" data-testid="signatures-section">
      <div>
        <h2 className="text-2xl font-semibold">E-posta imzaları</h2>
        <p className="text-muted-foreground mt-1">İletilerinize otomatik olarak eklenecek imzaları yönetin.</p>
      </div>

      {/* İmza sekmeleri */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-border" role="tablist">
        {signatures.map((s) => (
          <button
            key={s.id}
            role="tab"
            aria-selected={draft.id === s.id}
            onClick={() => select(s)}
            className={`pb-2.5 text-sm font-medium border-b-2 transition-colors ${
              draft.id === s.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`signature-tab-${s.id}`}
          >
            {s.name}
          </button>
        ))}
        {isNew && <span className="pb-2.5 text-sm font-medium border-b-2 border-primary text-primary">{draft.name || 'Yeni imza'}</span>}
        <button onClick={startNew} className="pb-2.5 text-sm font-medium text-primary flex items-center gap-1.5 hover:underline" data-testid="new-signature">
          <Plus className="h-4 w-4" /> Yeni imza
        </button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Düzenleyici */}
        <div className="rounded-xl border border-border p-5 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="signature-name">İmza adı</Label>
            <Input id="signature-name" value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} maxLength={120} data-testid="signature-name" />
          </div>

          <div className="flex gap-6 border-b border-border" role="tablist">
            {[['visual', 'Görsel düzenleyici'], ['html', 'HTML']].map(([id, label]) => (
              <button
                key={id}
                role="tab"
                aria-selected={editorTab === id}
                onClick={() => (id === 'html' ? openHtmlTab() : setEditorTab('visual'))}
                className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
                  editorTab === id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`editor-tab-${id}`}
              >
                {label}
              </button>
            ))}
          </div>

          {editorTab === 'visual' ? (
            <div className="space-y-4">
              {draft.template === 'custom' && (
                <p className="text-sm rounded-lg bg-amber-50 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200 px-3 py-2" data-testid="custom-notice">
                  Bu imza HTML olarak düzenlenmiş. Aşağıdan bir şablon seçerseniz içerik şablona göre yeniden oluşturulur.
                </p>
              )}
              <div className="space-y-2">
                <Label htmlFor="sig-logo">Logo veya fotoğraf (adres)</Label>
                <Input id="sig-logo" value={draft.fields.logoUrl} onChange={(e) => setField('logoUrl', e.target.value)} placeholder="https://... (boşsa baş harfler gösterilir)" data-testid="sig-logoUrl" />
                <p className="text-xs text-muted-foreground">Görsel mail içine gömülmez; alıcı açtığında bu adresten yüklenir.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {FIELD_LABELS.map(([key, label, placeholder]) => (
                  <div key={key} className="space-y-2">
                    <Label htmlFor={`sig-${key}`}>{label}</Label>
                    <Input id={`sig-${key}`} value={draft.fields[key]} onChange={(e) => setField(key, e.target.value)} placeholder={placeholder} data-testid={`sig-${key}`} />
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <Label htmlFor="sig-accent">Vurgu rengi</Label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={/^#[0-9a-fA-F]{6}$/.test(draft.fields.accent) ? draft.fields.accent : DEFAULT_ACCENT}
                    onChange={(e) => setField('accent', e.target.value)}
                    className="h-10 w-12 rounded-md border border-border bg-background p-1 cursor-pointer"
                    aria-label="Vurgu rengi seç"
                  />
                  <Input id="sig-accent" value={draft.fields.accent} onChange={(e) => setField('accent', e.target.value)} className="w-32" maxLength={7} data-testid="sig-accent" />
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <textarea
                value={draft.template === 'custom' ? draft.html : htmlOf(draft)}
                onChange={(e) => editHtml(e.target.value)}
                spellCheck={false}
                className="w-full min-h-[300px] rounded-md border border-border bg-background p-3 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-ring/30"
                aria-label="İmza HTML"
                data-testid="signature-html"
              />
              <p className="text-xs text-muted-foreground">Betikler (script) çalıştırılmaz. HTML'i düzenlerseniz görsel düzenleyici alanları artık kullanılmaz.</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-border">
            <div className="space-y-2">
              <Label htmlFor="default-new">Yeni e-postalar</Label>
              <select id="default-new" className={selectClass} value={defaultNew} onChange={(e) => applyDefault('new', e.target.value)} data-testid="default-new">
                <option value="">İmza kullanma</option>
                {signatures.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="default-reply">Yanıtlar ve iletiler</Label>
              <select id="default-reply" className={selectClass} value={defaultReply} onChange={(e) => applyDefault('reply', e.target.value)} data-testid="default-reply">
                <option value="">İmza kullanma</option>
                {signatures.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Önizleme + şablonlar */}
        <div className="space-y-6">
          <div className="rounded-xl border border-border overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <h3 className="font-semibold">Canlı önizleme</h3>
              <div className="flex rounded-lg border border-border overflow-hidden">
                {[['desktop', Monitor, 'Masaüstü'], ['mobile', Smartphone, 'Mobil']].map(([id, Icon, label]) => (
                  <button
                    key={id}
                    onClick={() => setPreviewMode(id)}
                    aria-label={label}
                    aria-pressed={previewMode === id}
                    className={`h-9 w-10 flex items-center justify-center ${previewMode === id ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-secondary'}`}
                  >
                    <Icon className="h-4 w-4" />
                  </button>
                ))}
              </div>
            </div>
            <div className="p-4 bg-secondary/40">
              <div className={`mx-auto rounded-lg border border-border overflow-hidden bg-white transition-all ${previewMode === 'mobile' ? 'max-w-[360px]' : ''}`}>
                <div className="px-4 py-2.5 text-xs text-gray-500 border-b border-gray-200 space-y-0.5">
                  <div><span className="text-gray-400">Kimden:</span> {user?.display_name || draft.fields.name} &lt;{user?.email}&gt;</div>
                  <div><span className="text-gray-400">Konu:</span> Yeni dönem hizmet teklifi</div>
                </div>
                <EmailBody key={`${draft.id}-${previewMode}`} html={previewDoc} testId="signature-preview-frame" />
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="font-semibold">İmza şablonu</h3>
            <div className="grid grid-cols-3 gap-3">
              {SIGNATURE_TEMPLATES.map((t) => {
                const active = draft.template === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => chooseTemplate(t.id)}
                    aria-pressed={active}
                    className={`rounded-lg border p-3 text-left transition-colors ${active ? 'border-primary bg-accent' : 'border-border hover:bg-secondary'}`}
                    data-testid={`template-${t.id}`}
                  >
                    <span className="flex items-center justify-between text-sm font-medium">
                      {t.name}
                      {active && <Check className="h-4 w-4 text-primary" />}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Alt çubuk */}
      <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-border">
        <div className="text-sm text-muted-foreground">
          {savedAt ? `Son kayıt: Bugün, ${savedAt.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}` : dirty ? 'Kaydedilmemiş değişiklikler var' : ''}
        </div>
        <div className="flex items-center gap-2">
          {saved && (
            <Button variant="ghost" className="text-destructive" onClick={remove} data-testid="delete-signature">
              <Trash2 className="h-4 w-4 mr-2" /> Sil
            </Button>
          )}
          <Button variant="outline" onClick={discard} disabled={!dirty || isSaving} data-testid="discard-signature">Vazgeç</Button>
          <Button onClick={save} disabled={!dirty || isSaving} data-testid="save-signature">
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Değişiklikleri kaydet
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SignaturesSection;
